/**
 * useShopConfig — the single source of facts for rendered copy.
 *
 * Copy dictionaries hold narrative with {placeholders}. This hook resolves
 * those placeholders from shop.config.json, so a fact changes in exactly
 * one place and every locale refreshes at once.
 *
 * If you are about to type a price, phone number, address, year, review
 * count or barber name into a component or a copy string — it belongs here
 * instead. See docs/COPY_STYLE.md.
 */

import { useMemo } from 'react';
import config from '../lib/config';
import { useLocale, interpolate, type Locale } from '../content';

const STATE_NAMES: Record<string, string> = { FL: 'Florida' };

/** Minutes -> "30 min" / "1 hr 15 min", localized. */
export function formatDuration(minutes: number, locale: Locale): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  // "min" is the same in both; "hr" is not — Spanish abbreviates hora as h.
  const minLabel = 'min';
  const hrLabel = locale === 'es' ? 'h' : 'hr';
  if (h === 0) return `${m} ${minLabel}`;
  if (m === 0) return `${h} ${hrLabel}`;
  return `${h} ${hrLabel} ${m} ${minLabel}`;
}

/** 1058 -> "1,058". Style guide: prices as $35, never $35.00. */
export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

export interface ShopFacts extends Record<string, string | number> {}

export function useShopConfig() {
  const locale = useLocale();

  return useMemo(() => {
    const { shop, wifi, reviews, barbers, services, add_ons: addOns, legal, notifications, kiosk } = config;

    const streetName = shop.address_line_1.replace(/^\d+\s+/, ''); // "Pure Water Way"
    const kenny = barbers.find((b) => b.slug === 'kenny');
    const works = services.find((s) => s.slug === 'the-works');
    const seniorFrom = services
      .filter((s) => s.slug.startsWith('senior-cut'))
      .reduce((min, s) => Math.min(min, s.price), Infinity);
    const minBarberRating = Math.min(...barbers.map((b) => b.reviews.rating));
    const currentYear = new Date().getFullYear();

    /** Flat map consumed by interpolate(). Every key here is a fact. */
    const facts: ShopFacts = {
      shopName: shop.name,
      shopAddress: `${shop.address_line_1}, ${shop.city}, ${shop.state} ${shop.zip}`,
      streetName,
      addressLine1: shop.address_line_1,
      city: shop.city,
      state: shop.state,
      stateFull: STATE_NAMES[shop.state] ?? shop.state,
      zip: shop.zip,
      plazaName: shop.plaza_name,
      crossStreet: shop.cross_street,
      foundedYear: shop.founded_year,
      yearsSinceFounding: currentYear - shop.founded_year,
      currentYear,
      shopPhone: shop.phone_display,
      shopPhoneDigits: shop.phone_display.replace(/[()\s]/g, '').replace(/^(\d{3})-?/, '$1-'),
      shopPhoneE164: shop.phone_e164,
      // Currently equal to the phone digits, but a separate fact on purpose:
      // when Kenny changes the wifi, the copy must not keep printing a phone
      // number at guests.
      wifiPassword: wifi.password,
      instagramHandle: shop.social.instagram.handle,
      barberCount: barbers.length,
      /** The crew minus the owner — "four barbers alongside him". */
      barbersBesidesOwner: Math.max(barbers.length - 1, 0),
      combinedReviewTotal: formatCount(reviews.combined_total),
      combinedRating: reviews.combined_rating,
      booksyReviewTotal: formatCount(reviews.booksy_total),
      googleReviewTotal: formatCount(reviews.google_total),
      googleRating: reviews.google_rating,
      minBarberRating,
      kennyYearsExperience: kenny?.years_experience ?? 0,
      worksPrice: works?.price_display ?? '',
      seniorFromPrice: `$${seniorFrom}`,
      // Shared with the Tier 3 GHL reminder workflows — one source, two surfaces.
      reminderPrimaryHours: notifications.reminders.primary_lead_hours,
      reminderSecondaryHours: notifications.reminders.secondary_lead_hours,
    };

    // {rafyYears}, {kennyYears}, ... — numbers are credibility (COPY_STYLE.md).
    // Only set when the config actually has a number; a barber without the
    // field gets no key, so the placeholder stays visible instead of blank.
    for (const b of barbers) {
      if (typeof b.years_experience === 'number' && Number.isFinite(b.years_experience)) {
        facts[`${b.slug}Years`] = b.years_experience;
      }
    }

    /** Resolve a copy template against the facts above. */
    const t = (template: string, extra: Record<string, string | number> = {}) =>
      interpolate(template, { ...facts, ...extra });

    return {
      config,
      locale,
      facts,
      t,
      shop,
      reviews,
      barbers,
      kiosk,
      services,
      addOns,
      legal,
      hours: shop.hours,
      notifications,
      formatDuration: (m: number) => formatDuration(m, locale),
      /**
       * Display name for a catalogue item. .name stays English because the
       * kiosk and the retired wizard render it directly and are out of
       * scope; name_es is the marketing site's label. Falls back to name,
       * so a new service without a translation shows English rather than
       * a hole.
       */
      serviceName: (item: { name: string; name_es?: string }) =>
        (locale === 'es' && item.name_es) || item.name,
    };
  }, [locale]);
}

export default useShopConfig;
